import { createContext, useContext } from 'react'
import { OF, type OfApi, type QueryValue } from '../ofApi'

/** OnlyFans' own users/me for the Account (the settings read most of their values from it). */
export type OfMe = Record<string, unknown>

/**
 * What every Settings section gets (onlyfans.com/my/settings, captured
 * 2026-10-22). Sections call OnlyFans exactly as its own pages do, through
 * `of` with `referer(id)`.
 */
export interface SettingsCtx {
  accountId: string
  of: OfApi
  /** users/me as OnlyFans last returned it. */
  me: OfMe
  reloadMe: () => Promise<void>
  /** PATCH users/me with only what changed; the answer replaces `me`. */
  patchMe: (body: Record<string, unknown>) => Promise<OfMe>
  /** Merge fields into `me` after another call changed them (no request). */
  mergeMe: (fields: OfMe) => void
  /** May change settings (the CRM's page.profile write). */
  canWrite: boolean
  /** Account owner or super admin: Account security, login sessions, cards. */
  isOwner: boolean
  /** Another settings section, by its OnlyFans path under /my/settings (e.g. `account/username`). */
  open: (section: string) => void
  /** Another CRM page of this Account (e.g. `collections` with `view=blocked`). */
  openPage: (page: string, query?: string) => void
  /** A one-line confirmation ("Saved"). */
  say: (text: string) => void
}

export const SettingsContext = createContext<SettingsCtx | null>(null)

export function useSettings(): SettingsCtx {
  const ctx = useContext(SettingsContext)
  if (!ctx) throw new Error('useSettings outside SettingsPage')
  return ctx
}

/** The page OnlyFans itself calls from: https://onlyfans.com/my/settings/<section>. */
export function settingsReferer(section: string): string {
  return `${OF}/my/settings/${section.replace(/^\/+|\/+$/g, '')}`
}

export type { QueryValue }

/** OnlyFans' error message from a failed call (gateway envelope or thrown Error). */
export function ofError(e: unknown, fallback: string): string {
  if (e && typeof e === 'object') {
    const err = e as { message?: unknown; error?: { message?: unknown }; detail?: unknown }
    const nested = err.error?.message
    if (typeof nested === 'string' && nested) return nested
    if (typeof err.message === 'string' && err.message) return err.message
    if (typeof err.detail === 'string' && err.detail) return err.detail
  }
  return fallback
}

export const asObj = (v: unknown): Record<string, unknown> => (v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : {})
export const asArr = (v: unknown): Record<string, unknown>[] => (Array.isArray(v) ? (v.filter((x) => x && typeof x === 'object') as Record<string, unknown>[]) : [])
export const asStr = (v: unknown): string => (typeof v === 'string' ? v : typeof v === 'number' ? String(v) : '')
export const asNum = (v: unknown): number => (typeof v === 'number' ? v : typeof v === 'string' && v.trim() !== '' && !Number.isNaN(Number(v)) ? Number(v) : 0)
export const asBool = (v: unknown): boolean => v === true || v === 1 || v === '1' || v === 'true'
